import { Fragment } from 'react';
import { InfiniteLoader, List, AutoSizer } from 'react-virtualized';
import PropTypes from 'prop-types';

import ActivityIndicator from 'src/components/ActivityIndicator';
import NoData from './NoData';

import { useTranslation } from 'src/hooks/useTranslation';

import * as styles from './index.module.scss';

const ELLIPSIS_LOADER_STYLE = {
  height: '50px',
  display: 'flex',
  padding: '20px',
};

/** Shared row placeholder for virtualized tables - loading skeleton */
const TableRowPlaceholder = ({ style, className }) => (
  <div style={style} className={className}>
    <ActivityIndicator type="Ellipsis" style={ELLIPSIS_LOADER_STYLE} />
  </div>
);

const Body = ({
  data,
  renderRow,
  renderRowPlaceHolder,
  height,
  isLoadingMore,
  registerChild,
  onRowsRendered,
  emptyMessage,
  columns,
  hasNoSearchResults,
}) => {
  return data?.length > 0 ? (
    <AutoSizer disableHeight>
      {({ width }) => (
        <List
          ref={registerChild}
          height={height}
          onRowsRendered={onRowsRendered}
          rowCount={data?.length + 1}
          rowHeight={60}
          width={width}
          rowRenderer={({ index, key, style }) => {
            if (index >= data?.length) {
              return isLoadingMore ? (
                <Fragment key={key}>
                  {renderRowPlaceHolder({
                    style,
                    className: styles.skeletonLoader,
                  })}
                </Fragment>
              ) : null;
            }

            const item = data[index];
            return (
              item && (
                <Fragment key={key}>
                  {renderRow({
                    item,
                    style,
                    className: styles.vTableRow,
                    columns,
                    itemClassName: styles.vTableRowItem,
                    index: key,
                  })}
                </Fragment>
              )
            );
          }}
        />
      )}
    </AutoSizer>
  ) : hasNoSearchResults ? (
    <NoData />
  ) : (
    <NoData emptyMessage={emptyMessage} />
  );
};

const BodyRenderer = ({
  isLoading,
  data,
  renderRow,
  renderRowPlaceHolder,
  height,
  isLoadingMore,
  registerChild,
  onRowsRendered,
  emptyMessage,
  columns,
  hasNoSearchResults,
}) => {
  return isLoading ? (
    <ActivityIndicator type="Table" />
  ) : (
    <Body
      renderRow={renderRow}
      renderRowPlaceHolder={renderRowPlaceHolder}
      height={height}
      isLoadingMore={isLoadingMore}
      registerChild={registerChild}
      onRowsRendered={onRowsRendered}
      data={data}
      emptyMessage={emptyMessage}
      columns={columns}
      hasNoSearchResults={hasNoSearchResults}
    />
  );
};

const Header = ({ columns, skipColumnHeaderTranslation, headerPrefixColumn }) => {
  const { t } = useTranslation();
  const columnWidths = columns
    .map((column) =>
      column.absoluteWidth ? column.absoluteWidth : `${column.width}fr`
    )
    .join(' ');
  const gridTemplateColumns = headerPrefixColumn
    ? `${headerPrefixColumn} ${columnWidths}`
    : columnWidths;
  return (
    <div
      className={styles.vTableHeader}
      style={{ gridTemplateColumns }}
    >
      {headerPrefixColumn && <span />}
      {columns.map((column) => (
        <p key={column.title} className={styles.vTableHeaderItem}>
          {skipColumnHeaderTranslation ? column.title : t(column.title)}
        </p>
      ))}
    </div>
  );
};

const ResizableVirtualizedTable = ({
  data,
  loadMoreRows,
  columns,
  isLoading,
  renderRow,
  renderRowPlaceHolder,
  height,
  isLoadingMore,
  emptyMessage,
  infiniteLoaderRef,
  noSearchCondition,
  skipColumnHeaderTranslation,
  headerPrefixColumn,
  'data-testid': dataTestId,
}) => {
  const hasNoSearchResults =
    !noSearchCondition && !isLoading && data?.length === 0;

  return (
    <InfiniteLoader
      isRowLoaded={({ index }) => index < data?.length}
      loadMoreRows={loadMoreRows}
      rowCount={1000000}
      ref={infiniteLoaderRef}
    >
      {({ onRowsRendered, registerChild }) => (
        <div
          className={styles.vTableContainer}
          role={dataTestId ? 'grid' : undefined}
          data-testid={dataTestId}
        >
          <div className={styles.vTable}>
            <Header
              columns={columns}
              skipColumnHeaderTranslation={skipColumnHeaderTranslation}
              headerPrefixColumn={headerPrefixColumn}
              key={JSON.stringify(columns) + (headerPrefixColumn || '')}
            />
            <BodyRenderer
              isLoading={isLoading}
              renderRow={renderRow}
              renderRowPlaceHolder={renderRowPlaceHolder}
              height={height}
              isLoadingMore={isLoadingMore}
              registerChild={registerChild}
              onRowsRendered={onRowsRendered}
              data={data}
              emptyMessage={emptyMessage}
              columns={columns}
              hasNoSearchResults={hasNoSearchResults}
            />
          </div>
        </div>
      )}
    </InfiniteLoader>
  );
};

ResizableVirtualizedTable.defaultProps = {
  columns: [],
  isLoading: false,
  height: (window.innerHeight ?? 800) - 310,
  emptyMessage: 'There are no users',
  renderRowPlaceHolder: ({ style, className }) => (
    <TableRowPlaceholder style={style} className={className} />
  ),
};

ResizableVirtualizedTable.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.array,
  isLoading: PropTypes.bool.isRequired,
  isLoadingMore: PropTypes.bool,
  renderRow: PropTypes.func.isRequired,
  renderRowPlaceHolder: PropTypes.func,
  loadMoreRows: PropTypes.func,
  height: PropTypes.number,
  emptyMessage: PropTypes.string,
  infiniteLoaderRef: PropTypes.object,
  noSearchCondition: PropTypes.bool,
  skipColumnHeaderTranslation: PropTypes.bool,
  headerPrefixColumn: PropTypes.string,
  'data-testid': PropTypes.string,
};

export default ResizableVirtualizedTable;
